<template>
  <div>
    <div class="d-inline-flex">
      <luci-btn-icon :config="addGameBtnConfig" @callback="$router.push({ name: 'GameCreate' })"></luci-btn-icon>
      <luci-btn-icon :config="refreshTableBtnConfig" @callback="getAllItems({ clearPrev: true })"></luci-btn-icon>
    </div>

    <v-alert v-if="error" :value="true" color="error" icon="error" class="error-container">
      <span class="error-span luci-text white--text">{{ error }}</span>
    </v-alert>

    <luci-table
      :loading="isLoading || deleteLoading || isActiveLoading"
      :config="gameListConfig"
      @edit-row-callback="editGame"
      @delete-row-callback="openConfirmModal"
      @field-change-callback="fieldChangeCallback"
      @field-click-callback="fieldClickCallback"
      @sort-by-callback="sortCallback">
    </luci-table>

    <confirm-modal
      :open="showConfirmModal"
      :title="confirmModal.title"
      :message="confirmModal.message"
      @yes-callback="deleteGame"
      @no-callback="closeConfirmModal">
    </confirm-modal>

    <message-modal
      data-test-key="messageModal"
      :open="showMsgModal"
      :title="msgModal.title"
      :message="msgModal.message"
      @ok-callback="resetMsgModal">
    </message-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { find, kebabCase, isEmpty, split, sortBy } from 'lodash'

import ConfirmModal from '../modal/ConfirmModal'
import MessageModal from '../modal/MessageModal'
import variables from '../../helpers/variables'

export default {
  computed: {
    ...mapGetters({
      error: 'game/list/error',
      items: 'game/list/items',
      isLoading: 'game/list/isLoading',
      tableItems: 'game/list/tableItems',
      brandHeaders: 'game/list/brandHeaders',
      deleteError: 'game/del/error',
      deleteLoading: 'game/del/isLoading',
      setActiveError: 'game/list/setActiveError',
      isActiveLoading: 'game/list/isActiveLoading'
    }),
    gameListConfig () {
      const tableConfig = {
        title: 'Games',
        showSearch: true,
        items: this.tableItems,
        headers: [
          { text: 'Name', value: 'data.name', sortable: true },
          { text: 'Active', value: 'data.active', width: '100px' },
          { text: 'Description', value: 'data.description' }
        ],
        pagination: { sortBy: 'data.name' },
        serverSorting: true,
        showButtonEdit: true,
        showButtonDelete: true,
        hideActions: true
      }

      // Adding brand headers to table if a game has a gameFile - need this to be able to add a play-link
      tableConfig.headers = tableConfig.headers.concat(this.brandHeaders)

      // Add actions column at the end AFTER all brand headers are added
      tableConfig.headers = tableConfig.headers.concat([{ text: 'Actions', value: 'actions' }])

      return tableConfig
    }
  },
  components: {
    ConfirmModal, MessageModal
  },
  data () {
    return {
      showMsgModal: false,
      msgModal: {},
      showConfirmModal: false,
      confirmModal: { title: '', message: '' },
      addGameBtnConfig: { type: 'button', name: 'add-game-btn', primary: true, icon: true, iconType: 'add', tooltip: 'Add a new game' },
      refreshTableBtnConfig: variables.refreshBtnIconOptions,
      game: null
    }
  },
  created () {
    this.getAllItems({})
  },
  methods: {
    ...mapActions({
      getAllItems: 'game/list/getAllItems',
      setAllItems: 'game/list/setAllItems',
      delete: 'game/del/del',
      setActive: 'game/list/setActive',
      setActiveCheckbox: 'game/list/setActiveCheckbox'
    }),
    editGame (item) {
      this.$router.push({ name: 'GameUpdate', params: { id: item['@id'] } })
    },
    openConfirmModal (item) {
      this.confirmModal = {
        title: 'Delete game',
        message: `Do you want to delete game: <b>${item.name}</b>?`
      }
      this.showConfirmModal = true
      this.game = item
    },
    closeConfirmModal () {
      this.showConfirmModal = false
      this.confirmModal = { title: '', message: '' }
      this.game = null
    },
    deleteGame () {
      this.showConfirmModal = false
      this.delete(this.game).then(() => {
        if (!this.deleteError) {
          this.game = null
          this.confirmModal = { title: '', message: '' }
          this.getAllItems({})
        } else {
          this.showMsgModal = true
        }
      })
    },
    fieldChangeCallback (field, value, item) {
      if (field === 'active') {
        this.setActiveCheckbox({ id: item['@id'], active: value, toggle: false })
        if (this.validateSettingActive(item, value)) {
          this.setActive({ id: item['@id'], active: value })
        } else {
          this.msgModal.title = 'Activation Failed'
          this.msgModal.message = 'This game cannot be ACTIVE until it has a valid: <b>name</b>, <b>description</b>, <b>category</b> and <b>territory</b>.'
          this.msgModal.okAction = { name: 'toggle_checkbox', id: item['@id'], value }
          this.showMsgModal = true
        }
      }
    },
    validateSettingActive (data, value) {
      if (value) {
        return data.name !== '' && data.description !== '' && !isEmpty(data.categories) && !isEmpty(data.territories)
      }
      return true
    },
    fieldClickCallback (field, item) {
      if (!isEmpty(item.gameFiles)) {
        const latestGameFile = find(item.gameFiles, (x) => kebabCase(x.brand.name) === kebabCase(field))
        if (latestGameFile) { window.open(latestGameFile.latestVersion.path) }
      }
    },
    sortCallback (column, descending) {
      const field = split(column, '.')[1]
      this.setAllItems({ items: sortBy(this.items, [field]) })
    },
    resetMsgModal () {
      if (this.msgModal.okAction && this.msgModal.okAction.name === 'toggle_checkbox') {
        this.setActiveCheckbox({ id: this.msgModal.okAction.id, active: this.msgModal.okAction.value, toggle: true })
      }
      this.showMsgModal = false
      this.msgModal = {}
    }
  }
}
</script>
